import { useState, useEffect, useLayoutEffect } from "react"
import { useTheme, Box, Grid, TextField, Checkbox, Typography, Accordion, AccordionSummary, AccordionDetails, Table, TableHead, TableCell, TableBody, TableRow, Button, ButtonBase } from "@mui/material"
import { AnimatePresence, motion, useMotionValue, useTransform } from "motion/react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Item, ItemIds } from "../../content/menu-items"
import { AnimatedNumber } from '../animated-elements/AnimatedNumber';
import { ClearRounded, InfoTwoTone, ShoppingCart } from "@mui/icons-material";
import { CustomTooltip } from "../modals/CustomTooltip";
import { Demodal } from "demodal";
import { useWindowDimensions } from "../../features/hooks/useWindowDimensions";


const textFieldStyles = {
    color: 'white',
    '& label': {
        color: 'red',
    },
    input: { color: 'white' },
    label: { color: 'white' },
    '& .Mui': { color: 'white' },

    '& label.Mui-focused': {
        color: 'white',
    },
    '& .MuiInput-underline:after': {
        borderBottomColor: 'yellow',
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: 'white',
        },
        '&:hover fieldset': {
            borderColor: 'white',
        },
        '&.Mui-focused fieldset': {
            borderColor: 'rgba(0, 217, 255, 1)',
        },
    },
}


interface CalculatorHUDProps {
    cart: Item[],
    handleSelectItem: Function
}

interface CalculatorState {
    numEntreesInCart: number,
    total: number,
    prevTotal: number,
    numPeople: number
}

export const CalculatorHUD: React.FC<CalculatorHUDProps> = ({cart, handleSelectItem}: CalculatorHUDProps) => {

    const theme = useTheme()
    const [inputValue, setInputValue] = useState<string>("Search")
    const { height, width } = useWindowDimensions()
    const [tableTextSize, setTableTextSize] = useState(0)
    const [onsite, setOnsite] = useState<boolean>(false)
    // const [error, setError] = useState("")
    const [calculatorState, setCalculatorState] = useState<CalculatorState>({numEntreesInCart: 0, total: 0, prevTotal: 0, numPeople: 0})
    const motionWidth = useMotionValue(width);

    const handleOnsiteChange = () => {
        setOnsite(!onsite)
    }

     const calculateTotal = () => {
        const perPersonBaseCost = 30
        const sortedCart = ([...cart].sort((a, b) => a.id - b.id))
        const numEntreesPresent = cart.filter((item)=> item.id >= ItemIds.Tritip && item.id <= ItemIds.PorkBelly ).length
        const discountPerPerson = calculatorState.numPeople >= 200?5: calculatorState.numPeople >= 150? 4: calculatorState.numPeople >= 100? 3: calculatorState.numPeople >= 50? 2: calculatorState.numPeople >= 20? 1: 0
        const basePrice = numEntreesPresent <= 0? 0: (perPersonBaseCost - discountPerPerson) * calculatorState.numPeople
        const entreeTotal = sortedCart.reduce((total, currentItem, index) =>  ( index > 0 )?total + currentItem.pricePerPerson: 0, 0) * calculatorState.numPeople
        const sidesTotal = numEntreesPresent <=0 ? (sortedCart.reduce((sidesTotal, currentItem) =>  ( !isItemEntree(currentItem))?sidesTotal + currentItem.pricePerPerson: 0, 0) * calculatorState.numPeople): 0
        const runningTotal = basePrice + entreeTotal + sidesTotal +(onsite? 100: 0 )

        setCalculatorState((prevState)=> ({ ...prevState, numEntreesInCart: numEntreesPresent, prevTotal: prevState.total, total: runningTotal }))
    }

    const isItemEntree = (item: Item) => {
        return item.id >= ItemIds.Tritip && item.id <= ItemIds.PorkBelly
    }

    function scrollToBottomOfScreen(element: HTMLElement) {
        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const elementBottom = rect.top + rect.height;
        const shiftByAmmount = width < 600? 50: 200
        const scrollPosition = window.scrollY + elementBottom - viewportHeight + shiftByAmmount;

        window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
        });
    }

    function handleRemoveItem(item: Item) {
        const element = document.getElementById(`${item.id}`)
        handleSelectItem(item)

        if( element )
            scrollToBottomOfScreen( element )
    }

    const handleKeyDown = ({evt, item, message, action, popoverPosition}:{evt: React.KeyboardEvent, item?: Item, message?: string, action: 'removeItem' | 'openPopover', popoverPosition?: 'left' | 'center' | 'right'}) => {
        if ((evt.key === 'Enter') || (evt.key === ' ')) {
            if(action === 'removeItem' && item){
                const element = document.getElementById(`${item.id}`)

                evt.preventDefault()
                handleSelectItem(item)

                if( element )
                    scrollToBottomOfScreen( element )
            }
            else if(action === 'openPopover'){
                if(item)
                    Demodal.open(CustomTooltip, {message: item.title, anchor: evt.target, direction: popoverPosition})
                else
                    Demodal.open(CustomTooltip, {message: message, anchor: evt.target, direction: popoverPosition})
            }
        }
    }

    const handleNumberOfPeopleChange = ({newNumPeople}: {newNumPeople: string | undefined}) => {
        const parsedNumPeople = parseInt(newNumPeople? newNumPeople: '0', 10)
        setCalculatorState((prevState)=> ({ ...prevState, numPeople: parsedNumPeople }))
    }

    useEffect(()=>{
        calculateTotal()
        //isSelectionValid()
    },[ calculatorState.numPeople, cart, onsite])

    useLayoutEffect(()=> {
        motionWidth.set(width)
    },[width])

    return(
        <Box 
            sx={{
                zIndex: 50, 
                backgroundColor: 'black', 
                filter: 'drop-shadow(0px 15px 10px rgba(0,0,0,.8))',
                borderBottom: '1px solid rgba(150,150,150, .8)',
                position: 'fixed',
                top:'0px',
                left:'0px',
            }}
            width='100vw'
            pt={{xs: '80px', md:'30px'}}

        >
            <Grid 
                container 
                justifySelf='center' 
                justifyContent='space-between' 
                alignItems='center' 
                maxWidth='1000px' 
                spacing={2} 
                width={{xs:'90%'}}
                px={{xs: 'auto', md: '5vw', lg: '0px'}}
                mx='auto'
            >
                <Grid item xs={6}>
                    <TextField 
                        type='number'
                        inputProps={{ min: 0 }}
                        sx={{...textFieldStyles}} 
                        label="# of People"
                        aria-label='Number of People Served.' 
                        value={calculatorState.numPeople.toString()} 
                        onChange={(e)=>{ handleNumberOfPeopleChange({newNumPeople: e.target.value})}}
                    />
                </Grid>
                <Grid item xs={6} sx={{ padding: 'auto', margin: 'auto', alignContent: 'flex-end', display: 'flex'}}>
                    <Grid container direction='column' sx={{paddingRight: '4%'}}>
                        <Grid item >
                            <motion.div style={{ cursor: 'pointer' }}>
                                <Typography color='white' textAlign='right'> 
                                <span
                                    tabIndex={0}
                                    onClick={(event)=>Demodal.open( CustomTooltip, { message: 'We bring the barbeque to your home! Two hours of on-site grilling and food service included in on-site fee.', anchor: event.target, direction: 'right' })}
                                    onKeyDown={(e)=>handleKeyDown({evt: e, action: 'openPopover', message: 'We bring the barbeque to your home! Two hours of on-site grilling and food service included in on-site fee.', popoverPosition: 'left'})}    
                                >
                                    <InfoTwoTone sx={{color: 'white', verticalAlign: 'middle', justifyContent:'flex-end'}}/> 
                                    <span style={{color: 'white', verticalAlign: 'middle', justifyContent:'flex-end'}}>
                                        On-Site
                                    </span>
                                </span>
                                </Typography>
                            </motion.div>
                        </Grid>
                        <Grid item justifyContent='flex-end' display='flex'>
                            <Checkbox
                                aria-label='Check box to request on site grilling.' 
                                onChange={handleOnsiteChange} 
                                size='medium' 
                                checked={onsite} 
                                sx={{color: 'white', paddingRight: '0px', marginRight:'0px', '&.Mui-checked': { color:'#a00000' }}}
                            />
                        </Grid> 
                    </Grid>
                </Grid>
                
                {/* Accordions containing Cart items and quantity */}
                <Grid container  justifyContent='flex-start' alignItems='flex-start'>
                    <Accordion  sx={{width:'100%', backgroundColor: 'black', top:'0px'}} >
                        <AccordionSummary
                            tabIndex={0}
                            expandIcon={<ExpandMoreIcon sx={{color: 'white'}}/>}
                            aria-controls="Items in cart"
                            id="Items cart header"
                            sx={{flexDirection: 'row-reverse', paddingRight:'0px', '&:focus': {border: '1px solid white', borderRadius: '12px 12px 0px 0px'}}}
                        >
                            <Grid container direction='row' justifyContent='space-between' alignItems='center       '>
                                <Grid item alignItems='center'>
                                    <ShoppingCart sx={{ color: 'white', verticalAlign:'middle' }}/> 
                                    <Typography component="span" sx={{marginRight:'10px', color: 'white', verticalAlign:'middle'}}>Cart</Typography> 
                                </Grid>
                                <Grid item sx={{paddingRight: '2%'}}>
                                    <span style={{color: 'white', fontSize: '18px'}}>
                                        Total:  $<AnimatedNumber fontSize='18px' from={calculatorState.prevTotal} to={calculatorState.total}/>
                                    </span>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails   tabIndex={0} sx={{  overflowY: 'auto', height: 'fit-contents', padding: '0px',  maxHeight: {xs: '25vh', md: '30vh'}, mb: '20px'}}>
                        {cart.length > 0?    
                            <Grid container  flexDirection='row' width='100%' sx={{overflowY: 'auto',}}>
       
                                <Table  
                                    sx={{
                                        border: '1px solid white',
                                        color: 'white', 
                                        overflowY: 'auto',
                                        "tr:nth-of-type(even)":{  backgroundColor:'rgba(45,45,45,1)' }, 
                                        "tr:nth-of-type(odd)":{  backgroundColor: 'rgba(85,85,85,1)' }, 
                                        th: {backgroundColor: 'black'} 
                                    }}
                                >
                                    <TableHead sx={{backgroundColor: 'black'}}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '40%' }}>
                                                Item
                                            </TableCell >
                                            <TableCell sx={{ fontWeight: 'bold', color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                Quantity
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                Price
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <AnimatePresence>
                                    {calculatorState.numEntreesInCart >= 1 &&
                                        <motion.tr  
                                            key={`cart-id-base-catering-cost`} 
                                            style={{ padding:'0px' }}
                                            whileHover={{ backgroundColor: 'black'}}
                                            initial={{ y: -50, opacity: 0 }} 
                                            animate={{ y: 0, opacity: 1, transition: { duration: 0.3 } }}
                                            exit={{ y: -50, opacity: 0, transition: { duration: 0.3 } }}
                                        >
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '40%' }}>
                                                <span
                                                    tabIndex={0} 
                                                    onClick={(e)=>Demodal.open(CustomTooltip, {message: 'Base Catering Cost', anchor: e.target, direction: 'left'})}
                                                    onKeyDown={(e)=>handleKeyDown({evt: e, action: 'openPopover', message: 'Base Catering Cost', popoverPosition: 'left'})}
                                                    style={{ 
                                                        display:'inline-block', 
                                                        width: `${motionWidth.get() * 0.20}px`, 
                                                        overflow:'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        paddingLeft: '12px',
                                                        verticalAlign: 'middle',
                                                        paddingTop: '4px',
                                                        paddingBottom: '4px'
                                                    }}
                                                >
                                                    Base Catering Cost
                                                </span>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                <span>{calculatorState.numPeople}</span>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                <span>${30 *calculatorState.numPeople}</span>
                                            </TableCell>
                                        </motion.tr>
                                    }
                                    {[...cart].sort((a, b) => a.id - b.id).map((item, index) =>
                                        <motion.tr
                                            key={`cart-id-${item.id}`} 
                                            id={`cart-id-${item.id}`}
                                            whileHover={{ backgroundColor: 'black'}}
                                            initial={{ y: -50, opacity: 0 }} 
                                            animate={{ y: 0 , opacity: 1, transition: { duration: 0.3 } }}
                                            exit={{ y: -50, opacity: 0, transition: { duration: 0.3 } }}
                                        >
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '40%'}}>
                                                <ButtonBase >
                                                    <ClearRounded
                                                        onClick={()=> handleRemoveItem(item)}
                                                        onKeyDown={(e)=>handleKeyDown({evt: e, item: item, action: 'removeItem'})}
                                                        tabIndex={0}  
                                                        color='error' 
                                                        sx={{
                                                            height: '20px',
                                                            width: '20px', 
                                                            padding:'0px', 
                                                            position: 'absolute', 
                                                            left: '-14px',
                                                            top: '-11px', 
                                                            borderRadius: '50%', 
                                                            border: '1px solid rgb(100,0,0)',
                                                            backgroundColor: '#333',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                border:'1px solid rgb(250,0,0)',
                                                                backgroundColor: '#555',
                                                                color: 'red'
                                                            },
                                                            '&:focus': {
                                                                border:'1px solid rgb(250,0,0)',
                                                                backgroundColor: '#555',
                                                                color: 'red'
                                                            }  
                                                        }}
                                                    />
                                                </ButtonBase>
                                                <span
                                                    tabIndex={0} 
                                                    onClick={(e)=>Demodal.open(CustomTooltip, {message: item.title, anchor: e.target, direction: 'left'})} 
                                                    onKeyDown={(e)=>handleKeyDown({evt: e, item: item, action: 'openPopover', popoverPosition: 'left'})}
                                                    style={{ 
                                                        display:'inline-block', 
                                                        width: `${motionWidth.get() * 0.20}px`, 
                                                        overflow:'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        verticalAlign: 'middle',
                                                        paddingTop: '4px',
                                                        paddingBottom: '4px',
                                                        paddingLeft: '12px',
                                                    }}
                                                >
                                                    {item.title}   
                                                </span>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                            {isItemEntree(item)?
                                                <span>{(calculatorState.numPeople/ calculatorState.numEntreesInCart).toFixed(2)} lbs</span>
                                                :
                                                <span>feeds {calculatorState.numPeople}</span>
                                            }
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                <span>
                                                {isItemEntree(item) && (index === 0)?
                                                    <>Included</>
                                                    :
                                                    <>${item.pricePerPerson * calculatorState.numPeople}</>
                                                }
                                                </span>
                                            </TableCell>         
                                        </motion.tr>
                                    )}
                                    {calculatorState.numEntreesInCart >= 1 &&
                                    <>
                                        <motion.tr 
                                            key={`cart-id-free-rice`} 
                                            style={{ padding:'0px' }}
                                            whileHover={{ backgroundColor: 'black'}}
                                            initial={{ y: -50, opacity: 0 }} 
                                            animate={{ y: 0 , opacity: 1, transition: { duration: 0.3 } }}
                                            exit={{ y: -50, opacity: 0, transition: { duration: 0.3 } }}
                                        >
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '40%' }}>
                                                <span 
                                                    style={{ display:'inline-block', paddingLeft: '12px', verticalAlign: 'middle', width: `${motionWidth.get() * 0.20 }px`, paddingTop: '4px', paddingBottom: '4px' }}
                                                    onClick={(e)=>Demodal.open(CustomTooltip, {message: 'Rice', anchor: e.target, direction: 'left'})}
                                                    onKeyDown={(e)=>handleKeyDown({evt: e, action: 'openPopover', message: 'Rice', popoverPosition: 'left'})}
                                                    tabIndex={0}
                                                >
                                                    Rice
                                                </span>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                <span>feeds {calculatorState.numPeople}</span>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                <span>Included</span>
                                            </TableCell>
                                        </motion.tr>
                                        <motion.tr 
                                            key={`cart-id-free-salad`} 
                                            style={{ padding:'0px' }}
                                            whileHover={{ backgroundColor: 'black'}}
                                            initial={{ y: -50, opacity: 0 }} 
                                            animate={{ y: 0 , opacity: 1, transition: { duration: 0.3 } }}
                                            exit={{ y: -50, opacity: 0, transition: { duration: 0.3 } }}
                                        >
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '40%' }}>
                                                <span 
                                                    onClick={(e)=>Demodal.open(CustomTooltip, {message: 'Cabbage Salad', anchor: e.target, direction: 'left'})}
                                                    onKeyDown={(e)=>handleKeyDown({evt: e, action: 'openPopover', message: 'Cabbage Salad', popoverPosition: 'left'})}
                                                    tabIndex={0}
                                                    style={{ 
                                                        display:'inline-block', 
                                                        width: `${motionWidth.get() * 0.20 }px`, 
                                                        overflow:'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        verticalAlign: 'middle',
                                                        paddingLeft: '12px',
                                                        paddingTop: '4px',
                                                        paddingBottom: '4px'
                                                    }}
                                                >
                                                    Cabbage Salad
                                                </span>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                <span>feeds {calculatorState.numPeople}</span>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                <span>Included</span>
                                            </TableCell>
                                        </motion.tr>
                                        <motion.tr 
                                            key={`cart-id-free-tzatziki`} 
                                            style={{ padding:'0px' }}
                                            whileHover={{ backgroundColor: 'black'}}
                                            initial={{ y: -50, opacity: 0 }} 
                                            animate={{ y: 0, opacity: 1, transition: { duration: 0.3 } }}
                                            exit={{ y: -50, opacity: 0, transition: { duration: 0.3 } }}
                                        >
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '40%' }}>
                                                <span 
                                                    onClick={(e)=>Demodal.open(CustomTooltip, {message: 'Tzatziki', anchor: e.target, direction: 'left'})}
                                                    onKeyDown={(e)=>handleKeyDown({evt: e, action: 'openPopover', message: 'Tzatziki', popoverPosition: 'left'})}
                                                    tabIndex={0}
                                                    style={{ 
                                                        display:'inline-block', 
                                                        width: `${motionWidth.get() * 0.20 }px`, 
                                                        overflow:'hidden', 
                                                        textOverflow: 'ellipsis', 
                                                        whiteSpace: 'nowrap',
                                                        verticalAlign: 'middle',
                                                        paddingLeft: '12px',
                                                        paddingTop: '4px',
                                                        paddingBottom: '4px'
                                                    }}
                                                >
                                                    Tzatziki
                                                </span>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                <span>feeds {calculatorState.numPeople}</span>
                                            </TableCell>
                                            <TableCell sx={{ color: 'white', paddingTop: '0px', paddingBottom: '0px', width: '30%' }}>
                                                <span>Included</span>
                                            </TableCell>
                                        </motion.tr>
                                    </>
                                    }
                                    </AnimatePresence>
                                    </TableBody>
                                </Table>
                            </Grid>
                            :
                            <Grid container  flexDirection='row' width='100%' sx={{color: 'white', justifyContent: 'center', fontWeight: 'bold', fontSize: '22px'}}>
                                Cart Empty
                            </Grid>
                        }
                        </AccordionDetails>
                    </Accordion>
                </Grid>
            </Grid>
        </Box>
    )
}
